<template>
  <card
    no-body
    class="public-booking-card"
    :class="{
      'public-booking-card--disabled': !hasAction,
      'public-booking-card--actions-only': quotationOnly || !!confirmedDate,
      'public-booking-card--unavailable':
        !quotationOnly && !!confirmedDate && !available,
    }"
    @click.native="$emit('open-card', $event)"
  >
    <template #image>
      <div
        class="public-booking-card-image"
        :class="{ 'public-booking-card-image--gallery': galleryUrls.length > 1 }"
        @click="onImageClick"
      >
        <div
          class="public-booking-card-image-frame"
          :class="{ 'is-fading': imageFading }"
        >
          <b-img-lazy
            :src="currentImage"
            :alt="product.name"
            fluid
          ></b-img-lazy>
        </div>
        <div
          class="public-booking-card-image-flash"
          :class="{ 'is-on': galleryRestarting }"
        ></div>
        <template v-if="galleryUrls.length > 1">
          <button
            type="button"
            class="public-booking-card-image-hit public-booking-card-image-hit--prev"
            title="Foto anterior"
            aria-label="Foto anterior"
            @click.stop="showPreviousImage"
          ></button>
          <button
            type="button"
            class="public-booking-card-image-hit public-booking-card-image-hit--next"
            title="Próxima foto"
            aria-label="Próxima foto"
            @click.stop="showNextImage"
          ></button>
          <div class="public-booking-card-dots">
            <span
              v-for="(url, idx) in galleryUrls"
              :key="idx"
              class="public-booking-card-dot"
              :class="{ 'public-booking-card-dot--active': idx === imageIndex }"
            ></span>
          </div>
        </template>
      </div>
    </template>

    <div class="public-booking-card-body">
      <ul v-if="valueItems.length" class="public-booking-card-meta public-booking-card-meta--value">
        <li v-for="item in valueItems" :key="item.key">
          <i :class="item.icon" :title="item.title" aria-hidden="true"></i>
          <span>{{ item.label }}</span>
        </li>
      </ul>

      <h3 class="public-booking-card-title">
        {{ product.name }}
      </h3>

      <div v-if="cityLabel" class="public-booking-card-city">
        <i
          class="fas fa-map-marker-alt"
          :title="xfieldPublicLabel('location_city')"
          aria-hidden="true"
        ></i>
        <span>{{ cityLabel }}</span>
      </div>

      <p
        v-if="shortDescription"
        class="public-booking-card-description"
      >
        {{ shortDescription }}
      </p>

      <ul v-if="profileItems.length" class="public-booking-card-meta">
        <li v-for="item in profileItems" :key="item.key">
          <i :class="item.icon" :title="item.title" aria-hidden="true"></i>
          <span>{{ item.label }}</span>
        </li>
      </ul>

      <div v-if="weekdayColumns.length" class="public-booking-card-schedule">
        <div class="public-booking-card-schedule-title">
          <i
            class="fas fa-calendar-check"
            :title="xfieldPublicLabel('availability')"
            aria-hidden="true"
          ></i>
          <span>horários fixos:</span>
        </div>
        <div
          class="public-booking-card-week"
          :style="{ gridTemplateColumns: 'repeat(' + weekdayColumns.length + ', 1fr)' }"
        >
          <div
            v-for="(day, dayIdx) in weekdayColumns"
            :key="'label-' + day.weekday"
            class="public-booking-card-week-label"
            :class="{ 'is-last': dayIdx === weekdayColumns.length - 1 }"
          >
            {{ day.label }}
          </div>
          <template v-for="(row, rowIdx) in weekdayTimeRows">
            <div
              v-for="(time, colIdx) in row"
              :key="'time-' + rowIdx + '-' + colIdx"
              class="public-booking-card-week-time"
            >
              {{ time }}
            </div>
          </template>
        </div>
      </div>

      <div v-if="specialDates.length" class="public-booking-card-schedule">
        <div class="public-booking-card-schedule-title">
          <i
            class="fas fa-calendar-day"
            :title="xfieldPublicLabel('availability')"
            aria-hidden="true"
          ></i>
          <span>datas especiais:</span>
        </div>
        <div class="public-booking-card-dates">
          <div
            v-for="item in specialDates"
            :key="item.sort"
            class="public-booking-card-date"
          >
            {{ item.label }}
          </div>
        </div>
      </div>

      <div
        v-if="confirmedDate"
        class="public-booking-card-actions"
      >
        <button
          v-if="available"
          type="button"
          class="btn public-booking-card-cta public-booking-card-cta--schedule"
          @click.stop="$emit('reserve')"
        >
          Reserva
        </button>
        <button
          v-if="hasQuotation && available"
          type="button"
          class="btn public-booking-card-cta public-booking-card-cta--quote"
          @click.stop="$emit('quote')"
        >
          Cotação
        </button>
        <button
          v-if="!available"
          type="button"
          class="btn public-booking-card-cta public-booking-card-cta--unavailable"
          disabled
        >
          {{ unavailableLabel }}
        </button>
      </div>
      <div
        v-else
        class="public-booking-card-actions"
      >
        <button
          type="button"
          class="btn public-booking-card-cta public-booking-card-cta--schedule"
          @click.stop="$emit('catalog-reserve')"
        >
          Reservar
        </button>
        <button
          v-if="hasQuotation"
          type="button"
          class="btn public-booking-card-cta public-booking-card-cta--quote"
          @click.stop="$emit('quote')"
        >
          Cotação
        </button>
      </div>
    </div>
  </card>
</template>

<script>
import moment from 'moment'
import nodeService from '@/services/nodejs/index'
import { fetchFileObjectUrl } from '@/store/utils/publicLogoFile'
import productXFields from '@/views/Products/store/product-xfields.json'

const WEEKDAY_ORDER = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
]

const WEEKDAY_LABEL = {
  monday: 'Seg',
  tuesday: 'Ter',
  wednesday: 'Qua',
  thursday: 'Qui',
  friday: 'Sex',
  saturday: 'Sáb',
  sunday: 'Dom',
}

const npsCache = {}

export default {
  name: 'PublicBookingCard',

  props: {
    product: {
      type: Object,
      required: true,
    },
    poi: {
      type: Object,
      default: null,
    },
    available: {
      type: Boolean,
      default: false,
    },
    hasAction: {
      type: Boolean,
      default: true,
    },
    hasQuotation: {
      type: Boolean,
      default: false,
    },
    confirmedDate: {
      type: String,
      default: '',
    },
    quotationOnly: {
      type: Boolean,
      default: false,
    },
    unavailableLabel: {
      type: String,
      default: '',
    },
    defaultImage: {
      type: String,
      default: '',
    },
    coverUrl: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      imageIndex: 0,
      galleryUrls: [],
      galleryObjectUrls: [],
      nps: null,
      imageFading: false,
      galleryRestarting: false,
      imageAnimating: false,
      imageTimer: null,
    }
  },

  computed: {
    productKey() {
      if (!this.product) return ''
      return this.product.uuid || this.product.product_uuid || this.product.id || ''
    },

    currentImage() {
      return (
        this.galleryUrls[this.imageIndex] ||
        this.coverUrl ||
        this.defaultImage
      )
    },

    shortDescription() {
      return this.plainText(this.resolveXFieldValue(this.product, 'short_description'))
    },

    cityLabel() {
      return this.fieldLabel(this.resolveXFieldValue(this.product, 'location_city'))
    },

    profileItems() {
      const items = []
      const age = this.fieldLabel(this.resolveXFieldValue(this.product, 'allowed_age'))
      if (age) {
        items.push({
          key: 'age',
          icon: 'fas fa-child',
          label: age,
          title: this.xfieldPublicLabel('allowed_age'),
        })
      }
      const difficulty = this.difficultyLabel(
        this.resolveXFieldValue(this.product, 'activity_difficulty')
      )
      if (difficulty) {
        items.push({
          key: 'difficulty',
          icon: 'fas fa-hiking',
          label: difficulty,
          title: this.xfieldPublicLabel('activity_difficulty'),
        })
      }
      const duration = this.fieldLabel(
        this.resolveXFieldValue(this.product, 'activity_duration')
      )
      if (duration) {
        items.push({
          key: 'duration',
          icon: 'fas fa-clock',
          label: duration,
          title: this.xfieldPublicLabel('activity_duration'),
        })
      }
      return items
    },

    valueItems() {
      const items = []
      if (this.npsLabel) {
        items.push({
          key: 'nps',
          icon: 'fas fa-star',
          label: this.npsLabel,
          title: this.xfieldPublicLabel('nps_group'),
        })
      }
      const price = this.priceLabel(this.resolveXFieldValue(this.product, 'activity_price'))
      if (price) {
        items.push({
          key: 'price',
          icon: 'fas fa-tag',
          label: price,
          title: this.xfieldPublicLabel('activity_price'),
        })
      }
      return items
    },

    npsLabel() {
      if (!this.nps || this.nps.average == null || this.nps.average === '') {
        return ''
      }
      const average = Number(this.nps.average)
      if (!Number.isFinite(average)) return ''
      const formatted = average.toLocaleString('pt-BR', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      })
      const total = Number(this.nps.total_responses)
      if (!Number.isFinite(total)) return formatted
      return `${formatted} (${total.toLocaleString('pt-BR')})`
    },

    parsedAvailability() {
      return this.parseAvailability(
        (this.poi && this.poi.availability) ||
          this.product.availability ||
          this.resolveXFieldValue(this.product, 'availability')
      )
    },

    weekdayColumns() {
      const availability = this.parsedAvailability
      if (!availability) return []
      const columns = []
      WEEKDAY_ORDER.forEach((weekday) => {
        const match = Object.entries(availability).find(([key, blocks]) => {
          if (!Array.isArray(blocks) || !blocks.length) return false
          return String(key).toLowerCase() === weekday
        })
        if (!match) return
        const times = this.timeList(match[1])
        if (!times.length) return
        columns.push({
          weekday,
          label: WEEKDAY_LABEL[weekday],
          times,
        })
      })
      return columns
    },

    weekdayTimeRows() {
      const max = this.weekdayColumns.reduce(
        (total, day) => Math.max(total, day.times.length),
        0
      )
      const rows = []
      for (let row = 0; row < max; row += 1) {
        rows.push(this.weekdayColumns.map((day) => day.times[row] || ''))
      }
      return rows
    },

    specialDates() {
      const availability = this.parsedAvailability
      if (!availability) return []
      const dates = []
      Object.entries(availability).forEach(([key, blocks]) => {
        if (!Array.isArray(blocks) || !blocks.length) return
        const keyDate = moment(key, 'YYYY-MM-DD', true)
        if (!keyDate.isValid()) return
        const times = this.timesLabel(blocks)
        if (!times) return
        dates.push({
          sort: keyDate.format('YYYY-MM-DD'),
          label: `${keyDate.format('DD/MM/YY')} - ${times}`,
        })
      })
      dates.sort((left, right) => left.sort.localeCompare(right.sort))
      return dates
    },
  },

  watch: {
    productKey: {
      immediate: true,
      handler() {
        this.imageIndex = 0
        this.imageFading = false
        this.galleryRestarting = false
        this.imageAnimating = false
        this.loadGallery()
        this.loadNps()
      },
    },
    coverUrl() {
      if (!this.galleryUrls.length && this.coverUrl) {
        this.galleryUrls = [this.coverUrl]
      }
    },
  },

  beforeDestroy() {
    this.clearImageTimers()
    this.clearGalleryObjectUrls()
  },

  methods: {
    onImageClick(event) {
      if (this.galleryUrls.length <= 1) return
      event.stopPropagation()
      const rect = event.currentTarget.getBoundingClientRect()
      const x = event.clientX - rect.left
      if (x < rect.width / 2) this.showPreviousImage()
      else this.showNextImage()
    },

    showPreviousImage() {
      if (this.galleryUrls.length <= 1) return
      const next =
        (this.imageIndex - 1 + this.galleryUrls.length) % this.galleryUrls.length
      this.goToImage(next, false)
    },

    showNextImage() {
      if (this.galleryUrls.length <= 1) return
      const wrapping = this.imageIndex === this.galleryUrls.length - 1
      const next = wrapping ? 0 : this.imageIndex + 1
      this.goToImage(next, wrapping)
    },

    goToImage(nextIndex, restart) {
      if (this.imageAnimating || nextIndex === this.imageIndex) return
      this.imageAnimating = true
      this.clearImageTimers()
      if (restart) {
        this.galleryRestarting = true
        this.imageTimer = setTimeout(() => {
          this.imageIndex = nextIndex
          this.imageTimer = setTimeout(() => {
            this.galleryRestarting = false
            this.imageAnimating = false
          }, 260)
        }, 220)
        return
      }
      this.imageFading = true
      this.imageTimer = setTimeout(() => {
        this.imageIndex = nextIndex
        this.$nextTick(() => {
          this.imageTimer = setTimeout(() => {
            this.imageFading = false
            this.imageAnimating = false
          }, 40)
        })
      }, 180)
    },

    clearImageTimers() {
      if (this.imageTimer) {
        clearTimeout(this.imageTimer)
        this.imageTimer = null
      }
    },

    xfieldPublicLabel(slug) {
      const field = productXFields[slug]
      if (!field || field.public_label == null || field.public_label === '') {
        return ''
      }
      return String(field.public_label).split('#')[0].trim()
    },

    fieldLabel(value) {
      if (value == null || value === '') return ''
      if (Array.isArray(value)) {
        return value.map((item) => this.fieldLabel(item)).filter(Boolean).join(', ')
      }
      if (typeof value === 'object') {
        return this.fieldLabel(value.label || value.name || value.value || '')
      }
      return String(value).trim()
    },

    difficultyLabel(value) {
      const raw = this.fieldLabel(value)
      if (!raw) return ''
      return raw.split(':')[0].trim()
    },

    priceLabel(value) {
      if (value == null || value === '') return ''
      const raw = this.fieldLabel(value)
      if (!raw) return ''
      if (/R\$/.test(raw)) return raw
      const digits = String(raw).replace(/[^\d,.-]/g, '')
      const number =
        digits.indexOf(',') >= 0
          ? Number(digits.replace(/\./g, '').replace(',', '.'))
          : Number(digits)
      if (!Number.isFinite(number) || number <= 0) return raw
      return number.toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      })
    },

    plainText(value) {
      if (value == null || value === '') return ''
      const tmp = document.createElement('div')
      tmp.innerHTML = String(value)
      return (tmp.textContent || tmp.innerText || '').replace(/\s+/g, ' ').trim()
    },

    resolveXFieldValue(product, key) {
      if (!product || !key) return null

      const unwrap = (raw) => {
        if (raw == null) return null
        if (typeof raw === 'object' && raw.value !== undefined) return raw.value
        return raw
      }

      if (product[key] != null && product[key] !== '') {
        return unwrap(product[key])
      }

      let xfields = product.xfields
      if (typeof xfields === 'string') {
        try {
          xfields = JSON.parse(xfields)
        } catch (error) {
          return null
        }
      }
      if (!xfields) return null

      if (xfields[key] != null) {
        return unwrap(xfields[key])
      }

      const laravel = xfields.laravel_data
      if (laravel && typeof laravel.value === 'string') {
        try {
          const parsed = JSON.parse(laravel.value)
          if (parsed && parsed[key] != null) return unwrap(parsed[key])
        } catch (error) {
          /* laravel_data.value is not JSON */
        }
      }
      if (laravel && laravel[key] != null) {
        return unwrap(laravel[key])
      }

      return null
    },

    unwrapImageSource(source) {
      if (Array.isArray(source)) return source[0]
      if (source && typeof source === 'object') {
        return (
          source.url ||
          source.src ||
          source.filepath ||
          source.value ||
          source.label ||
          ''
        )
      }
      return source
    },

    normalizeImageUrl(source) {
      const value = this.unwrapImageSource(source)
      if (typeof value !== 'string' || !value.trim()) return ''
      const trimmed = value.trim()
      if (trimmed.indexOf('google.com') > 0) {
        const match = /d\/([^/]+)\/view/.exec(trimmed)
        if (match && match[1]) {
          return `https://drive.google.com/thumbnail?id=${match[1]}&sz=w400`
        }
      }
      return trimmed
    },

    isDirectMediaUrl(value) {
      if (!value || typeof value !== 'string') return false
      return (
        value.indexOf('http://') === 0 ||
        value.indexOf('https://') === 0 ||
        value.indexOf('blob:') === 0 ||
        value.indexOf('data:image') === 0
      )
    },

    parseImageList(raw) {
      let value = raw
      if (typeof value === 'string') {
        try {
          value = JSON.parse(value)
        } catch (error) {
          value = value
            .split(/\n|,/)
            .map((item) => item.trim())
            .filter(Boolean)
        }
      }
      if (!value) return []
      return (Array.isArray(value) ? value : [value])
        .map((item) => this.normalizeImageUrl(item))
        .filter(Boolean)
    },

    imageSources() {
      const sources = []
      const seen = {}
      const push = (url) => {
        if (!url || seen[url]) return
        seen[url] = true
        sources.push(url)
      }
      push(this.normalizeImageUrl(this.product.cover))
      push(this.normalizeImageUrl(this.resolveXFieldValue(this.product, 'cover')))
      this.parseImageList(this.product.images).forEach(push)
      this.parseImageList(this.resolveXFieldValue(this.product, 'images')).forEach(
        push
      )
      if (this.coverUrl) {
        sources.unshift(this.coverUrl)
        return sources.filter((url, idx) => sources.indexOf(url) === idx)
      }
      return sources
    },

    async loadGallery() {
      const key = this.productKey
      this.clearGalleryObjectUrls()
      const sources = this.imageSources()
      const urls = []
      const objectUrls = []
      for (let i = 0; i < sources.length; i += 1) {
        const source = sources[i]
        if (this.isDirectMediaUrl(source)) {
          urls.push(source)
          continue
        }
        try {
          const objectUrl = await fetchFileObjectUrl(source)
          if (objectUrl) {
            objectUrls.push(objectUrl)
            urls.push(objectUrl)
          }
        } catch (error) {
          /* skip broken gallery item */
        }
      }
      if (this.productKey !== key) {
        objectUrls.forEach((url) => URL.revokeObjectURL(url))
        return
      }
      this.galleryObjectUrls = objectUrls
      this.galleryUrls = urls.length
        ? urls
        : this.coverUrl
        ? [this.coverUrl]
        : []
      if (this.imageIndex >= this.galleryUrls.length) this.imageIndex = 0
    },

    clearGalleryObjectUrls() {
      this.galleryObjectUrls.forEach((url) => {
        URL.revokeObjectURL(url)
      })
      this.galleryObjectUrls = []
    },

    parseAvailability(raw) {
      if (!raw) return null
      if (typeof raw === 'string') {
        try {
          const parsed = JSON.parse(raw)
          return parsed && typeof parsed === 'object' ? parsed : null
        } catch (error) {
          return null
        }
      }
      return typeof raw === 'object' ? raw : null
    },

    formatTime(start) {
      const parsed = moment(String(start), ['HH:mm:ss', 'HH:mm', 'H:mm'], true)
      if (!parsed.isValid()) return ''
      const minutes = parsed.format('mm')
      return minutes === '00' ? `${parsed.format('H')}h` : `${parsed.format('H')}h${minutes}`
    },

    timeList(blocks) {
      return (blocks || [])
        .map((block) => this.formatTime(block && block.start))
        .filter(Boolean)
    },

    timesLabel(blocks) {
      return this.timeList(blocks).join(' | ')
    },

    async loadNps() {
      const uuid = this.productKey
      this.nps = null
      if (!uuid) return
      const cacheKey = String(uuid).toUpperCase()
      if (npsCache[cacheKey] !== undefined) {
        this.nps = npsCache[cacheKey]
        return
      }
      try {
        const res = await nodeService.getProductNps(uuid)
        const data = res && res.data ? res.data : null
        npsCache[cacheKey] = data
        if (this.productKey === uuid) this.nps = data
      } catch (error) {
        npsCache[cacheKey] = null
        this.nps = null
      }
    },
  },
}
</script>

<style scoped>
.public-booking-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
  display: flex;
  flex-direction: column;
  height: 100%;
  margin-bottom: 0;
}

.public-booking-card--disabled {
  cursor: default;
}

.public-booking-card--unavailable {
  opacity: 0.72;
  cursor: default;
}

.public-booking-card--unavailable .public-booking-card-image >>> img {
  filter: grayscale(1);
}

.public-booking-card:not(.public-booking-card--disabled):hover {
  transform: translateY(-2px);
  border-color: #12576e;
  box-shadow: 0 8px 24px rgba(20, 103, 130, 0.12);
}

.public-booking-card.public-booking-card--actions-only {
  cursor: default;
}

.public-booking-card.public-booking-card--actions-only:hover {
  transform: none;
  border-color: #e2e8f0;
  box-shadow: none;
}

.public-booking-card-image {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 10;
  overflow: hidden;
  background-color: #edf2f7;
}

.public-booking-card-image-frame {
  width: 100%;
  height: 100%;
  transition: opacity 0.28s ease;
}

.public-booking-card-image-frame.is-fading {
  opacity: 0;
}

.public-booking-card-image >>> img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.public-booking-card-image-flash {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: #fff;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.22s ease;
  z-index: 2;
}

.public-booking-card-image-flash.is-on {
  opacity: 1;
}

.public-booking-card-image-hit {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 50%;
  border: 0;
  padding: 0;
  margin: 0;
  background: transparent;
  cursor: pointer;
  z-index: 3;
}

.public-booking-card-image-hit--prev {
  left: 0;
  cursor: w-resize;
}

.public-booking-card-image-hit--next {
  right: 0;
  cursor: e-resize;
}

.public-booking-card-image-hit--prev:hover {
  background: linear-gradient(
    to right,
    rgba(15, 23, 42, 0.18),
    transparent
  );
}

.public-booking-card-image-hit--next:hover {
  background: linear-gradient(
    to left,
    rgba(15, 23, 42, 0.18),
    transparent
  );
}

.public-booking-card-dots {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 8px;
  display: flex;
  justify-content: center;
  gap: 5px;
  pointer-events: none;
  z-index: 4;
}

.public-booking-card-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.55);
  box-shadow: 0 0 0 1px rgba(15, 23, 42, 0.2);
}

.public-booking-card-dot--active {
  background: #fff;
}

.public-booking-card-body {
  padding: 16px;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
}

.public-booking-card-city {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: #334155;
  font-size: 0.7rem;
  margin: 0 0 8px;
}

.public-booking-card-city i {
  color: #111;
  font-size: .9rem;
  width: 1.15rem;
  text-align: center;
}

.public-booking-card-title {
  color: #1e293b;
  font-size: 1.05rem;
  font-weight: 700;
  margin: 0 0 6px;
  line-height: 1.35;
}

.public-booking-card-description {
  color: #64748b;
  font-size: 0.9rem;
  line-height: 1.45;
  margin: 0 0 12px;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  border-bottom: 1px dashed #12576e;
  padding-bottom: 5px;
}

.public-booking-card-meta {
  list-style: none;
  margin: 0 0 8px;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0;
  color: #475569;
  font-size: 0.75rem;
}

.public-booking-card-meta--value {
  margin: 0 0 8px;
}

.public-booking-card-meta li {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}

.public-booking-card-meta li:not(:last-child)::after {
  content: '–';
  margin: 0 8px 0 8px;
  color: #cbd5e1;
}

.public-booking-card-meta i {
  color: #12576e;
  width: 14px;
  text-align: center;
}

.public-booking-card-schedule {
  margin: 0 0 12px;
}

.public-booking-card-schedule-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 6px;
  color: #1f2a37;
  font-size: 0.82rem;
  font-weight: 600;
}

.public-booking-card-schedule-title i {
  color: #12576e;
  width: 16px;
  text-align: center;
}

.public-booking-card-week {
  display: grid;
  column-gap: 0;
  row-gap: 2px;
  margin: 0;
  text-align: center;
  color: #334155;
  font-size: 0.75rem;
  line-height: 1.3;
}

.public-booking-card-week-label {
  position: relative;
  font-weight: 700;
  color: #12576e;
    padding: 0 0px 5px;
}

.public-booking-card-week-label:not(.is-last)::after {
  content: '|';
  position: absolute;
  right: -1px;
  top: 0;
  color: #cbd5e1;
  font-weight: 400;
}

.public-booking-card-week-time {
    padding: 0 0px 5px;
  color: #475569;
  font-size: .7rem;
}

.public-booking-card-dates {
  margin: 0;
  color: #334155;
  font-size: 0.82rem;
  line-height: 1.45;
}

.public-booking-card-date {
  margin: 0;
}

.public-booking-card-actions {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: auto;
}

.public-booking-card-cta {
  width: 100%;
  font-weight: 600;
  font-size: 0.95rem;
  padding: 10px 16px;
}

.public-booking-card-cta--schedule {
  color: #fff;
  background: #12576e;
  border: 1px solid #12576e;
}

.public-booking-card-cta--schedule:hover,
.public-booking-card-cta--schedule:focus {
  color: #fff;
  background: #0e4658;
  border-color: #0e4658;
}

.public-booking-card-cta--quote {
  color: #12576e;
  background: #fff;
  border: 1px solid #12576e;
}

.public-booking-card-cta--quote:hover,
.public-booking-card-cta--quote:focus {
  color: #fff;
  background: #12576e;
  border-color: #12576e;
}

.public-booking-card-cta--unavailable,
.public-booking-card-cta--unavailable:disabled {
  color: #8898aa;
  background: #f6f7f9;
  border: 1px solid #e2e8f0;
  cursor: not-allowed;
  opacity: 1;
}
</style>
